import { useState, useMemo, useRef } from "react";
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameDay,
  isWeekend,
  differenceInCalendarDays,
  parseISO,
  addMonths,
} from "date-fns";
import { Calendar as CalIcon } from "lucide-react";
import type { CalendarEvent, Calendar } from "@/hooks/useCalendar";

type GanttScale = "week" | "month" | "3month" | "6month";

interface CalendarGanttViewProps {
  currentDate: Date;
  events: CalendarEvent[];
  calendars: Calendar[];
  onEventClick: (event: CalendarEvent) => void;
}

const LABEL_WIDTH = 220;
const ROW_HEIGHT = 36;
const DAY_WIDTH: Record<GanttScale, number> = {
  week: 80,
  month: 38,
  "3month": 14,
  "6month": 7,
};

function getScaleRange(date: Date, scale: GanttScale) {
  switch (scale) {
    case "week":
      return { start: startOfWeek(date), end: endOfWeek(date) };
    case "month":
      return { start: startOfMonth(date), end: endOfMonth(date) };
    case "3month":
      return { start: startOfMonth(date), end: endOfMonth(addMonths(date, 2)) };
    case "6month":
      return { start: startOfMonth(date), end: endOfMonth(addMonths(date, 5)) };
  }
}

export function CalendarGanttView({
  currentDate,
  events,
  calendars,
  onEventClick,
}: CalendarGanttViewProps) {
  // Note: this component manages its own scale state independently
  const [scale, setScale] = useState<GanttScale>("month");
  const scrollRef = useRef<HTMLDivElement>(null);
  const labelRef = useRef<HTMLDivElement>(null);

  const calendarMap = useMemo(() => {
    const map: Record<string, Calendar> = {};
    for (const c of calendars) map[c.id] = c;
    return map;
  }, [calendars]);

  const { start: rangeStart, end: rangeEnd } = useMemo(
    () => getScaleRange(currentDate, scale),
    [currentDate, scale]
  );

  const days = useMemo(
    () => eachDayOfInterval({ start: rangeStart, end: rangeEnd }),
    [rangeStart, rangeEnd]
  );

  const dayWidth = DAY_WIDTH[scale];
  const totalWidth = days.length * dayWidth;
  const now = new Date();

  // Sort events: start date asc, then longer events first
  const sortedEvents = useMemo(() => {
    return [...events]
      .filter((ev) => {
        const evEnd = parseISO(ev.end_at);
        const evStart = parseISO(ev.start_at);
        return evEnd >= rangeStart && evStart <= rangeEnd;
      })
      .sort((a, b) => {
        const aStart = parseISO(a.start_at).getTime();
        const bStart = parseISO(b.start_at).getTime();
        if (aStart !== bStart) return aStart - bStart;
        return (parseISO(b.end_at).getTime() - bStart) - (parseISO(a.end_at).getTime() - aStart);
      });
  }, [events, rangeStart, rangeEnd]);

  const getBarStyle = (ev: CalendarEvent) => {
    const evStart = parseISO(ev.start_at);
    const evEnd = parseISO(ev.end_at);
    const clampedStart = evStart < rangeStart ? rangeStart : evStart;
    const clampedEnd = evEnd > rangeEnd ? rangeEnd : evEnd;
    const startOffset = differenceInCalendarDays(clampedStart, rangeStart);
    const duration = Math.max(1, differenceInCalendarDays(clampedEnd, clampedStart) + (ev.all_day ? 0 : 1));
    return {
      left: startOffset * dayWidth,
      width: duration * dayWidth - 2,
    };
  };

  const todayOffset = differenceInCalendarDays(now, rangeStart);
  const showTodayLine = todayOffset >= 0 && todayOffset < days.length;

  // Month header spans for 3M/6M scales
  const monthHeaders = useMemo(() => {
    if (scale === "week") return [];
    const headers: { label: string; left: number; width: number }[] = [];
    let curMonth = -1;
    let monthStartIdx = 0;
    days.forEach((day, i) => {
      const m = day.getMonth() + day.getFullYear() * 12;
      if (m !== curMonth) {
        if (curMonth >= 0) {
          headers.push({
            label: format(days[monthStartIdx], scale === "month" ? "MMMM yyyy" : "MMM yyyy"),
            left: monthStartIdx * dayWidth,
            width: (i - monthStartIdx) * dayWidth,
          });
        }
        curMonth = m;
        monthStartIdx = i;
      }
    });
    headers.push({
      label: format(days[monthStartIdx], scale === "month" ? "MMMM yyyy" : "MMM yyyy"),
      left: monthStartIdx * dayWidth,
      width: (days.length - monthStartIdx) * dayWidth,
    });
    return headers;
  }, [days, dayWidth, scale]);

  const gridHeight = Math.max(sortedEvents.length * ROW_HEIGHT, 300);

  // Sync vertical scroll between label column and grid
  const handleGridScroll = () => {
    if (scrollRef.current && labelRef.current) {
      labelRef.current.scrollTop = scrollRef.current.scrollTop;
    }
  };

  return (
    <div className="border rounded-lg overflow-hidden bg-card h-full flex flex-col">
      {/* Scale toolbar */}
      <div className="flex-none flex items-center gap-2 px-3 py-2 border-b bg-muted/30">
        <span className="text-xs text-muted-foreground mr-1">Scale:</span>
        {(["week", "month", "3month", "6month"] as GanttScale[]).map((s) => (
          <button
            key={s}
            className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
              scale === s
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:bg-accent"
            }`}
            onClick={() => setScale(s)}
          >
            {s === "week" ? "Wk" : s === "month" ? "Mo" : s === "3month" ? "3M" : "6M"}
          </button>
        ))}
        <div className="flex-1" />
        <span className="text-xs font-medium text-muted-foreground">
          {format(rangeStart, "MMM d")} – {format(rangeEnd, "MMM d, yyyy")}
        </span>
      </div>

      {/* Main area */}
      <div className="flex-1 min-h-0 flex">
        {/* Left: event labels */}
        <div
          className="flex-none border-r bg-card overflow-hidden"
          style={{ width: LABEL_WIDTH }}
          ref={labelRef}
        >
          {/* Header spacer matching the date header */}
          <div className="sticky top-0 z-10 bg-card border-b" style={{ height: scale === "week" ? 40 : 44 }} />

          <div className="overflow-hidden">
            {sortedEvents.length === 0 ? (
              <div className="px-3 py-8 text-xs text-muted-foreground text-center">
                No events in this range
              </div>
            ) : (
              sortedEvents.map((ev) => {
                const cal = calendarMap[ev.calendar_id];
                const isOOO = calendarMap[ev.calendar_id]?.slug === "cal_ooo";
                return (
                  <div
                    key={ev.id}
                    className="flex items-center gap-2 px-3 border-b border-border/10 cursor-pointer hover:bg-muted/50 transition-colors"
                    style={{ height: ROW_HEIGHT }}
                    onClick={() => onEventClick(ev)}
                    title={ev.title}
                  >
                    <CalIcon
                      className="h-3.5 w-3.5 flex-shrink-0"
                      style={{ color: cal?.color || "#94a3b8" }}
                    />
                    <span className="text-xs truncate">
                      {isOOO ? <span className="font-semibold text-muted-foreground">Out · </span> : null}
                      {ev.title}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Right: timeline */}
        <div
          className="flex-1 min-w-0 overflow-auto"
          ref={scrollRef}
          onScroll={handleGridScroll}
        >
          <div style={{ width: totalWidth, minHeight: "100%" }}>
            {/* Date header — sticky */}
            <div
              className="sticky top-0 z-10 bg-card border-b shadow-[0_2px_4px_-1px_rgba(0,0,0,0.1)]"
              style={{ height: scale === "week" ? 40 : 44 }}
            >
              {/* Month row (for non-week scales) */}
              {scale !== "week" && (
                <div className="relative h-5">
                  {monthHeaders.map((mh, i) => (
                    <div
                      key={i}
                      className="absolute top-0 text-[10px] font-semibold text-foreground/70 border-r border-border/30 flex items-center px-2 truncate h-5"
                      style={{ left: mh.left, width: mh.width }}
                    >
                      {mh.label}
                    </div>
                  ))}
                </div>
              )}

              {/* Day numbers row */}
              <div
                className={`relative flex ${scale === "week" ? "h-10" : "h-[24px]"}`}
              >
                {days.map((day, i) => {
                  const isToday = isSameDay(day, now);
                  // Show labels based on scale density
                  const showLabel =
                    scale === "week" ||
                    scale === "month" ||
                    (scale === "3month" && (day.getDate() === 1 || day.getDate() % 5 === 0)) ||
                    (scale === "6month" && (day.getDate() === 1 || day.getDate() === 15));

                  return (
                    <div
                      key={i}
                      className={`flex-none border-r border-border/20 flex items-center justify-center ${
                        isWeekend(day) ? "bg-muted/20" : ""
                      }`}
                      style={{ width: dayWidth }}
                    >
                      {showLabel && (
                        <span
                          className={`text-[10px] leading-none ${
                            isToday
                              ? "bg-primary text-primary-foreground rounded-full min-w-[18px] h-[18px] flex items-center justify-center font-semibold"
                              : "text-muted-foreground"
                          }`}
                        >
                          {format(day, "d")}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Grid body */}
            <div className="relative" style={{ height: gridHeight }}>
              {/* Vertical day lines */}
              {days.map((day, i) => (
                <div
                  key={i}
                  className={`absolute top-0 border-r ${
                    isWeekend(day)
                      ? "bg-muted/10 border-border/20"
                      : "border-border/10"
                  }`}
                  style={{
                    left: i * dayWidth,
                    width: dayWidth,
                    height: gridHeight,
                  }}
                />
              ))}

              {/* Today line */}
              {showTodayLine && (
                <div
                  className="absolute top-0 w-0.5 bg-red-500 z-10"
                  style={{
                    left: todayOffset * dayWidth + dayWidth / 2,
                    height: gridHeight,
                  }}
                />
              )}

              {/* Event bars */}
              {sortedEvents.map((ev, rowIndex) => {
                const bar = getBarStyle(ev);
                const cal = calendarMap[ev.calendar_id];
                const color = cal?.color || "#94a3b8";
                const isOOO = calendarMap[ev.calendar_id]?.slug === "cal_ooo";

                return (
                  <div
                    key={ev.id}
                    className="absolute flex items-center"
                    style={{
                      top: rowIndex * ROW_HEIGHT + 4,
                      height: ROW_HEIGHT - 8,
                      left: bar.left + 1,
                      width: Math.max(bar.width, 4),
                    }}
                  >
                    <div
                      className="h-full w-full rounded cursor-pointer hover:opacity-80 transition-opacity flex items-center px-2 overflow-hidden"
                      style={{
                        backgroundColor: color,
                        backgroundImage: isOOO
                          ? "repeating-linear-gradient(45deg,transparent,transparent 4px,rgba(255,255,255,0.25) 4px,rgba(255,255,255,0.25) 5px)"
                          : undefined,
                        opacity: 0.85,
                      }}
                      onClick={() => onEventClick(ev)}
                      title={ev.title}
                    >
                      {bar.width > 60 && (
                        <span className="text-[11px] font-medium text-white truncate">
                          {isOOO ? "Out · " : ""}{ev.title}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Row separator lines */}
              {sortedEvents.map((_, i) => (
                <div
                  key={`row-${i}`}
                  className="absolute left-0 right-0 border-b border-border/10"
                  style={{ top: (i + 1) * ROW_HEIGHT }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
